/**
 * ValueChainRiver — Visualisation horizontale "rivière" de la chaîne de valeur.
 *
 * V23.15.29 — Composant SVG natif (zéro dépendance) qui dessine un cours d'eau
 * allant de l'amont (gauche) vers l'aval (droite). Chaque nœud est une entité
 * réelle (entreprise, institution). Le nœud "self" est mis en évidence.
 *
 * Architecture : props-driven et indépendant du backend. Conçu pour être
 * réutilisé tel quel dans la Cartographie des dépendances (juste passer un
 * autre tableau `nodes`).
 *
 * Props:
 *   - nodes: [{id, name, role, flow_position (0-100), is_self, capture_pct,
 *             negotiation_weight_pct, notes, branch, source}]
 *   - selectedId?: string — id du nœud actuellement sélectionné
 *   - onSelectNode?: (node) => void
 *   - onResetToSelf?: () => void — appelé quand on clique "Revenir au centre"
 *   - height?: number (default 280)
 *   - testIdPrefix?: string (default "river")
 */
import { useMemo, useRef, useState, useCallback } from "react";

const COLOR_AMONT = "#7F77DD"; // amont = violet
const COLOR_SELF = "#EF9F27"; // self = orange Noam
const COLOR_AVAL = "#1D9E75"; // aval = vert

const VIEW_W = 1000;
const PADDING_X = 60;
const PADDING_Y = 40;

function nodeColor(node) {
  if (node.is_self) return COLOR_SELF;
  return node.flow_position < 50 ? COLOR_AMONT : COLOR_AVAL;
}

function nodeRadius(node) {
  return node.is_self ? 18 : 11;
}

export default function ValueChainRiver({
  nodes = [],
  selectedId,
  onSelectNode,
  onResetToSelf,
  onMoveNode,           // V23.15.31 — (nodeId, newFlowPosition) => Promise, drag & drop
  isWhatIfMode = false, // V23.15.31 — active drag mode + visual hints
  height = 280,
  testIdPrefix = "river",
}) {
  const svgRef = useRef(null);
  const [dragState, setDragState] = useState(null); // { nodeId, currentX } during drag
  const [dragBusy, setDragBusy] = useState(false);
  // V23.16.9 — Zoom + pan navigation
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panState, setPanState] = useState(null);

  const layout = useMemo(() => {
    const sorted = [...(nodes || [])].sort((a, b) => (a.flow_position || 0) - (b.flow_position || 0));
    const usableW = VIEW_W - PADDING_X * 2;
    const usableH = height - PADDING_Y * 2;
    // V23.15.30 — Anti-collision : si plusieurs nœuds tombent dans la même bande
    // horizontale (différence < 60 px), on alterne le décalage vertical du label
    // (dy positif/négatif) pour éviter qu'ils se chevauchent.
    const positioned = sorted.map((n) => {
      const x = PADDING_X + (Number(n.flow_position || 50) / 100) * usableW;
      const branch = n.is_self ? 0 : Number(n.branch || 0);
      const branchOffset = branch === 0 ? 0 : (branch % 2 === 1 ? -1 : 1) * Math.ceil(branch / 2) * (usableH / 5);
      const y = PADDING_Y + usableH / 2 + branchOffset;
      return { ...n, x, y, label_stagger: 0 };
    });
    // Détection de cluster : si un nœud précédent est à < 60px sur la même ligne y,
    // on décale son label sous le voisin (stagger croissant).
    for (let i = 0; i < positioned.length; i++) {
      const cur = positioned[i];
      let stagger = 0;
      for (let j = 0; j < i; j++) {
        const prev = positioned[j];
        if (Math.abs(prev.x - cur.x) < 60 && Math.abs(prev.y - cur.y) < 6) {
          stagger = Math.max(stagger, (prev.label_stagger || 0) + 1);
        }
      }
      cur.label_stagger = stagger;
    }
    return positioned;
  }, [nodes, height]);

  // Path principal de la rivière : courbe Bézier passant par les self/centraux
  const riverPath = useMemo(() => {
    const usableH = height - PADDING_Y * 2;
    const midY = PADDING_Y + usableH / 2;
    const left = PADDING_X - 20;
    const right = VIEW_W - PADDING_X + 20;
    // Sinuosité très légère pour évoquer un flux
    return `M ${left} ${midY}
            C ${VIEW_W * 0.3} ${midY - 30}, ${VIEW_W * 0.7} ${midY + 30}, ${right} ${midY}`;
  }, [height]);

  // Génération des "embranchements" : un trait courbe partant de la rivière vers chaque nœud branché
  const branchPaths = useMemo(() => {
    const usableH = height - PADDING_Y * 2;
    const midY = PADDING_Y + usableH / 2;
    return layout
      .filter((n) => !n.is_self && Math.abs(n.y - midY) > 2)
      .map((n, i) => ({
        d: `M ${n.x} ${midY} Q ${n.x} ${(n.y + midY) / 2}, ${n.x} ${n.y}`,
        key: `branch-${n.id || i}`,
      }));
  }, [layout, height]);

  // V23.16.9 — Navigation : zoom au wheel, pan au drag (sauf en mode What-if).
  const handleWheel = useCallback((e) => {
    if (isWhatIfMode) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.15 : -0.15;
    setZoom((z) => Math.max(0.5, Math.min(3.5, z + delta)));
  }, [isWhatIfMode]);

  const handleCanvasPointerDown = useCallback((e) => {
    if (isWhatIfMode || dragState) return;
    setPanState({ startX: e.clientX, startY: e.clientY, baseX: pan.x, baseY: pan.y });
  }, [isWhatIfMode, dragState, pan]);

  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const viewBoxW = VIEW_W / zoom;
  const viewBoxH = height / zoom;
  const viewBoxX = (VIEW_W - viewBoxW) / 2 - pan.x;
  const viewBoxY = (height - viewBoxH) / 2 - pan.y;

  // V23.15.31 — Conversion x SVG → flow_position (0-100) — hooks AVANT tout early return
  const xToFlowPosition = useCallback((svgX) => {
    const usableW = VIEW_W - PADDING_X * 2;
    return Math.round(Math.max(0, Math.min(100, ((svgX - PADDING_X) / usableW) * 100)));
  }, []);

  const getSvgPoint = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }, []);

  if (!nodes || nodes.length === 0) {
    return (
      <div className="text-zinc-500 text-sm italic" data-testid={`${testIdPrefix}-empty`}>
        Aucun nœud — générez ou ajoutez des acteurs pour afficher la rivière.
      </div>
    );
  }

  const selfNode = layout.find((n) => n.is_self);

  const handlePointerDown = (e, node) => {
    if (!isWhatIfMode || !onMoveNode || dragBusy) return;
    e.preventDefault();
    e.stopPropagation();
    const p = getSvgPoint(e.clientX, e.clientY);
    setDragState({ nodeId: node.id, currentX: p.x });
    // V23.15.31 — capture sur le SVG root pour stabilité quand le pointer dérive
    // hors du <g> pendant le drag (sinon move/up peuvent être perdus).
    try {
      if (svgRef.current && svgRef.current.setPointerCapture) {
        svgRef.current.setPointerCapture(e.pointerId);
      }
    } catch (_e) {
      // pointer capture not supported — silently ignore
    }
  };

  const handlePointerMove = (e) => {
    if (panState) {
      const dx = (e.clientX - panState.startX) / zoom;
      const dy = (e.clientY - panState.startY) / zoom;
      setPan({ x: panState.baseX + dx, y: panState.baseY + dy });
      return;
    }
    if (!dragState) return;
    const p = getSvgPoint(e.clientX, e.clientY);
    setDragState((d) => (d ? { ...d, currentX: p.x } : d));
  };

  const handlePointerUp = async (e) => {
    if (panState) {
      setPanState(null);
      return;
    }
    if (!dragState) return;
    const finalP = getSvgPoint(e.clientX, e.clientY);
    const nodeId = dragState.nodeId;
    const newFlow = xToFlowPosition(finalP.x);
    setDragState(null);
    // V23.15.57 — Libère explicitement le pointer capture pour permettre
    // un nouveau drag immédiat (certains navigateurs ne le relâchent pas
    // automatiquement à pointerup quand le SVG est re-render entre-temps).
    try {
      if (svgRef.current && svgRef.current.releasePointerCapture && e.pointerId != null) {
        svgRef.current.releasePointerCapture(e.pointerId);
      }
    } catch (_e) {
      // pointer capture already released — silently ignore
    }
    if (onMoveNode) {
      setDragBusy(true);
      try {
        await onMoveNode(nodeId, newFlow);
      } finally {
        setDragBusy(false);
      }
    }
  };

  return (
    <div className="relative w-full" data-testid={testIdPrefix}>
      <svg
        ref={svgRef}
        viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxW} ${viewBoxH}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full"
        style={{
          height: `${height}px`,
          userSelect: "none",
          touchAction: isWhatIfMode ? "none" : "pan-x pan-y",
          cursor: panState ? "grabbing" : (isWhatIfMode ? "default" : "grab"),
          background: "radial-gradient(ellipse at center, rgba(127,119,221,0.06) 0%, rgba(10,10,10,0) 70%)",
        }}
        onPointerDown={handleCanvasPointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => { setDragState(null); setPanState(null); }}
        onWheel={handleWheel}
      >
        {/* V23.16.9 — Defs : gradients enrichis + glow filter + water flow animation */}
        <defs>
          <linearGradient id="river-gradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={COLOR_AMONT} stopOpacity="0.35" />
            <stop offset="50%" stopColor={COLOR_SELF} stopOpacity="0.55" />
            <stop offset="100%" stopColor={COLOR_AVAL} stopOpacity="0.35" />
          </linearGradient>
          <linearGradient id="river-stroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={COLOR_AMONT} stopOpacity="0.9" />
            <stop offset="50%" stopColor={COLOR_SELF} stopOpacity="0.9" />
            <stop offset="100%" stopColor={COLOR_AVAL} stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="river-shimmer" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
            <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
            <animate attributeName="x1" values="-1;1;-1" dur="6s" repeatCount="indefinite" />
            <animate attributeName="x2" values="0;2;0" dur="6s" repeatCount="indefinite" />
          </linearGradient>
          <filter id="node-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Légendes amont/aval (subtiles, sur les côtés) */}
        <text x={PADDING_X - 35} y={PADDING_Y - 18} fill="#7F77DD" fontSize="10" fontWeight="600" letterSpacing="1" textAnchor="start">
          AMONT
        </text>
        <text x={VIEW_W - PADDING_X + 35} y={PADDING_Y - 18} fill="#1D9E75" fontSize="10" fontWeight="600" letterSpacing="1" textAnchor="end">
          AVAL
        </text>

        {/* Rivière : trait épais sous-jacent + filet visible + shimmer animé */}
        <path d={riverPath} fill="none" stroke="url(#river-gradient)" strokeWidth="32" strokeLinecap="round" opacity="0.85" />
        <path d={riverPath} fill="none" stroke="url(#river-stroke)" strokeWidth="2" strokeLinecap="round" />
        <path d={riverPath} fill="none" stroke="url(#river-shimmer)" strokeWidth="6" strokeLinecap="round" opacity="0.6" />

        {/* Embranchements vers les nœuds hors-axe */}
        {branchPaths.map((b) => (
          <path
            key={b.key}
            d={b.d}
            fill="none"
            stroke="rgba(255,255,255,0.18)"
            strokeWidth="1.2"
            strokeDasharray="3 3"
          />
        ))}

        {/* Nœuds */}
        {layout.map((n) => {
          const isSelected = selectedId === n.id;
          const isDragging = dragState && dragState.nodeId === n.id;
          const color = nodeColor(n);
          const r = nodeRadius(n);
          const labelMaxLen = n.is_self ? 22 : 18;
          const shortLabel = (n.name || "").length > labelMaxLen
            ? (n.name || "").slice(0, labelMaxLen - 1) + "…"
            : (n.name || "");
          // V23.15.31 — durant un drag, le nœud "ghosté" suit la position du pointeur
          const renderedX = isDragging ? dragState.currentX : n.x;
          return (
            <g
              key={n.id || `${n.name}-${n.flow_position}`}
              transform={`translate(${renderedX}, ${n.y})`}
              onClick={(e) => {
                if (dragState) return; // ignore click during drag
                e.stopPropagation();
                if (onSelectNode) onSelectNode(n);
              }}
              onPointerDown={(e) => handlePointerDown(e, n)}
              style={{
                cursor: isWhatIfMode && onMoveNode ? (isDragging ? "grabbing" : "grab") : (onSelectNode ? "pointer" : "default"),
                opacity: isDragging ? 0.85 : 1,
              }}
              data-testid={`${testIdPrefix}-node-${n.id || n.name}`}
            >
              {/* Halo selected */}
              {isSelected && (
                <circle r={r + 8} fill={color} opacity="0.18" />
              )}
              {/* Halo self avec glow filter */}
              {n.is_self && (
                <circle r={r + 5} fill={color} opacity="0.25" filter="url(#node-glow)">
                  <animate attributeName="r" values={`${r + 4};${r + 10};${r + 4}`} dur="3s" repeatCount="indefinite" />
                </circle>
              )}
              <circle r={r} fill={color} stroke="#0A0A0A" strokeWidth="1.5" filter={n.is_self ? "url(#node-glow)" : undefined} />
              {n.is_self && (
                <text fontSize="11" fontWeight="700" fill="#0A0A0A" textAnchor="middle" dy="4">
                  ★
                </text>
              )}
              <text
                y={r + 14 + (n.label_stagger || 0) * 13}
                fontSize={n.is_self ? "12" : "10"}
                fill={n.is_self ? "#FFFFFF" : "#D4D4D8"}
                fontWeight={n.is_self ? "600" : "500"}
                textAnchor="middle"
                style={{ pointerEvents: "none" }}
              >
                {shortLabel}
              </text>
              {n.role && (
                <text
                  y={r + 27 + (n.label_stagger || 0) * 13}
                  fontSize="9"
                  fill="#71717A"
                  textAnchor="middle"
                  style={{ pointerEvents: "none" }}
                >
                  {(n.role || "").slice(0, 22)}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* V23.15.31 — Indicateur drag/whatif busy */}
      {dragBusy && (
        <div className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/40">
          NOAM recalcule…
        </div>
      )}
      {isWhatIfMode && !dragBusy && (
        <div className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded bg-[#7F77DD]/15 text-[#7F77DD] border border-[#7F77DD]/40 inline-flex items-center gap-1 max-w-[260px] truncate" data-testid={`${testIdPrefix}-whatif-hint`} style={{ marginRight: 80 }}>
          Mode What-if · glissez un acteur pour simuler
        </div>
      )}

      {/* V23.16.9 — Navigation : zoom + reset view */}
      {!isWhatIfMode && (
        <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-md border border-white/10 p-0.5" data-testid={`${testIdPrefix}-nav-controls`}>
          <button
            onClick={() => setZoom((z) => Math.min(3.5, z + 0.3))}
            className="text-xs w-6 h-6 rounded text-zinc-300 hover:bg-white/10 hover:text-white"
            title="Zoom +"
            data-testid={`${testIdPrefix}-zoom-in`}
          >+</button>
          <button
            onClick={() => setZoom((z) => Math.max(0.5, z - 0.3))}
            className="text-xs w-6 h-6 rounded text-zinc-300 hover:bg-white/10 hover:text-white"
            title="Zoom -"
            data-testid={`${testIdPrefix}-zoom-out`}
          >−</button>
          <div className="w-px h-4 bg-white/10 mx-0.5" />
          <button
            onClick={resetView}
            className="text-[10px] px-2 h-6 rounded text-zinc-300 hover:bg-white/10 hover:text-white"
            title="Recadrer la vue"
            data-testid={`${testIdPrefix}-reset-view`}
          >Recadrer</button>
          <span className="text-[9px] text-zinc-500 px-1.5">{Math.round(zoom * 100)}%</span>
        </div>
      )}

      {/* Bouton "Revenir au centre" si self existe */}
      {selfNode && onResetToSelf && (
        <button
          onClick={() => {
            onResetToSelf();
            if (onSelectNode) onSelectNode(selfNode);
          }}
          className="absolute top-1 left-1/2 -translate-x-1/2 text-[10px] px-2.5 py-1 rounded-full bg-[#EF9F27]/15 border border-[#EF9F27]/40 text-[#EF9F27] hover:bg-[#EF9F27]/25 transition inline-flex items-center gap-1"
          data-testid={`${testIdPrefix}-reset-to-self`}
          title="Revenir à la position de votre organisation"
        >
          ★ Recentrer
        </button>
      )}
    </div>
  );
}
